/**
 * contractKanbanColumns - Definição única das colunas fixas do Kanban de Contratos
 *
 * As colunas do Kanban são fixas e representam os status do processo de geração
 * de contrato. Os status intermediários (fila/processamento) são agrupados na
 * coluna equivalente, mas o card continua exibindo o rótulo real do status.
 *
 * Esta definição espelha a função SQL `contract_status_group_members`.
 *
 * @version 1.1.0
 * RELEASE: 2026-08-12 v1.1.0 - Adicionado `headerClass` (tint suave no cabeçalho da coluna),
 *          espelhando o Kanban de Negócios (header colorido / card neutro)
 * RELEASE: 2026-08-12 v1.0.0 - Criação inicial
 */
import type { ContractStatus } from '@/types/contracts';

/** Identificadores das colunas (grupos de status) */
export type ContractStatusGroup =
  | 'draft'
  | 'generated'
  | 'sent'
  | 'signed'
  | 'expired'
  | 'cancelled'
  | 'error';

/** Metadados de uma coluna do Kanban */
export interface ContractKanbanColumnDef {
  /** Chave do grupo (enviada para a RPC) */
  group: ContractStatusGroup;
  /** Rótulo exibido no cabeçalho da coluna */
  label: string;
  /** Cor sólida usada nos indicadores (bolinha) da coluna */
  barClass: string;
  /** Fundo suave (tint) aplicado ao cabeçalho da coluna */
  headerClass: string;
  /** Status do banco que pertencem a esta coluna */
  statuses: ContractStatus[];
}

/**
 * Colunas fixas, na ordem do processo de geração de contrato.
 */
export const CONTRACT_KANBAN_COLUMNS: ContractKanbanColumnDef[] = [
  {
    group: 'draft',
    label: 'Rascunho',
    headerClass: 'bg-muted-foreground/10',
    barClass: 'bg-muted-foreground/40',
    statuses: ['draft'],
  },
  {
    group: 'generated',
    label: 'PDF Gerado',
    headerClass: 'bg-blue-500/10',
    barClass: 'bg-blue-500',
    statuses: ['generated', 'queued_pdf', 'processing_pdf'],
  },
  {
    group: 'sent',
    label: 'Enviado',
    headerClass: 'bg-amber-500/10',
    barClass: 'bg-amber-500',
    statuses: ['sent', 'queued_signature', 'processing_signature'],
  },
  {
    group: 'signed',
    label: 'Assinado',
    headerClass: 'bg-green-500/10',
    barClass: 'bg-green-500',
    statuses: ['signed'],
  },
  {
    group: 'expired',
    label: 'Expirado',
    headerClass: 'bg-red-500/10',
    barClass: 'bg-red-500',
    statuses: ['expired'],
  },
  {
    group: 'cancelled',
    label: 'Cancelado',
    headerClass: 'bg-destructive/10',
    barClass: 'bg-destructive',
    statuses: ['cancelled'],
  },
  {
    group: 'error',
    label: 'Erro',
    headerClass: 'bg-red-500/10',
    barClass: 'bg-red-500',
    statuses: ['error'],
  },
];

/** Mapa status -> coluna, usado pelo Realtime para mover o card de coluna */
export const CONTRACT_STATUS_TO_GROUP: Record<string, ContractStatusGroup> =
  CONTRACT_KANBAN_COLUMNS.reduce((acc, col) => {
    col.statuses.forEach((s) => {
      acc[s] = col.group;
    });
    return acc;
  }, {} as Record<string, ContractStatusGroup>);

/** Status que indicam processamento em andamento (spinner no card) */
export const CONTRACT_PROCESSING_STATUSES = new Set<string>([
  'queued_pdf',
  'processing_pdf',
  'queued_signature',
  'processing_signature',
]);

/**
 * Resolve o grupo/coluna de um status. Retorna null quando o status é desconhecido.
 */
export function resolveContractStatusGroup(status?: string | null): ContractStatusGroup | null {
  if (!status) return null;
  return CONTRACT_STATUS_TO_GROUP[status] ?? null;
}
